import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useRequests } from '../../requests/useRequests'
import { RefreshActions } from '../../state/refresh'
import { State } from '../../types/state'
import { Package } from '../../enums/package.enum'
import { Project } from '../../types/project'
import { LinearProgress, Box, Typography, Tooltip, IconButton } from '@mui/material'
import { SideMenu } from '../Header/SiteMenu'
import AppNavBar from '../Header/AppNavBar'

const statusOptions = [
  { value: 'all', label: 'Wszystkie' },
  { value: 'in_progress', label: 'W realizacji' },
  { value: 'completed', label: 'Zakończone' }
]

const sortOptions = [
  { value: 'createdAt', label: 'Data utworzenia' },
  { value: 'updatedAt', label: 'Ostatnia edycja' },
  { value: 'name', label: 'Nazwa' }
]

const ProjectsList = () => {
  const { package: userPackage, packageExpiredAt } = useSelector((state: State) => state.user)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('all') // 'all', 'in_progress', 'completed'
  const [sortBy, setSortBy] = useState('updatedAt') // 'createdAt', 'updatedAt', 'name'
  const [sortOrder, setSortOrder] = useState('desc') // 'asc', 'desc'
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const searchTimeoutRef = useRef<NodeJS.Timeout>()
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  // Aktualizujemy wyszukiwanie przy zmianie wartości
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchQuery(value)

    // Czyścimy poprzedni timeout jeśli istnieje
    if (searchTimeoutRef.current != null) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Ustawiamy nowy timeout
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(value)
    }, 500)
  }

  // Czyścimy timeout przy odmontowaniu komponentu
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current != null) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    fetchProjects()
  }, [activeTab, sortBy, sortOrder, debouncedSearchQuery])

  const fetchProjects = async () => {
    setIsLoading(true)
    try {
      const params: { sortBy: string, sortOrder: string, status?: string, searchQuery?: string } = {
        sortBy,
        sortOrder
      }

      if (activeTab !== 'all') {
        params.status = activeTab
      }

      if (debouncedSearchQuery) {
        params.searchQuery = debouncedSearchQuery
      }

      const response = await requestMaker.getProjects(params)
      setProjects(response.data || [])
    } catch (error) {
      console.error('Błąd podczas pobierania projektów', error)
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się pobrać listy projektów'
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  const handleDeleteProject = async (id: string) => {
    if (window.confirm('Czy na pewno chcesz usunąć ten projekt?')) {
      try {
        await requestMaker.deleteProject(id)
        setProjects(projects.filter(project => project.id !== id))
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Projekt został usunięty pomyślnie'
          })
        )
      } catch (error) {
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Nie udało się usunąć projektu'
          })
        )
      }
    }
  }

  const handleCopyProject = async (id: string) => {
    try {
      const response = await requestMaker.copyProject(id)
      setProjects(prev => [...prev, response.data])
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Projekt został skopiowany pomyślnie'
        })
      )
      fetchProjects()
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się skopiować projektu'
        })
      )
    }
  }

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const response = await requestMaker.createProject({ name: newProjectName });
      setNewProjectName('');
      setShowNewProjectModal(false);
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Projekt został utworzony pomyślnie'
        })
      );
      navigate(`/projects/${response.data.id}?tab=products`);
      fetchProjects();
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się utworzyć projektu'
        })
      );
    }
  };

  const getProjectsLimit = () => {
    if (userPackage === Package.START) return 30
    if (userPackage === Package.PLUS || userPackage === Package.PREMIUM) return 100000000
    return 0
  }

  if (isLoading) {
    return (
      <div className='loading-container'>
        <div className='spinner-border text-primary' role='status'>
          <span className='sr-only'>Ładowanie...</span>
        </div>
        <p className='mt-3'>Ładowanie projektów...</p>
      </div>
    )
  }

  return (
    <>
      <AppNavBar
        rightIcons={[]}
      />
      <div style={{ display: 'flex', minHeight: 'calc(100vh)', backgroundColor: '#f8f9fa' }}>
        <SideMenu />
        <section className='projects-list pt-40 pb-40' style={{ flex: 1, overflow: 'auto', padding: '0 24px' }}>
          <div className='container' style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div className='row mb-20'>
              <div className='col-12'>
                <div className='projects-controls' style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
                  {/* Wyszukiwarka */}
                  <div className='form-group mb-0' style={{ minWidth: 200 }}>
                    <label htmlFor='searchQuery' style={{ fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'block' }}>Szukaj:</label>
                    <input
                      type='text'
                      id='searchQuery'
                      className='form-control form-control-sm'
                      placeholder='Wyszukaj projekt...'
                      value={searchQuery}
                      onChange={handleSearchChange}
                      style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
                    />
                  </div>
                  {/* Filtrowanie po statusie */}
                  <div className='form-group mb-0' style={{ minWidth: 100 }}>
                    <label htmlFor='statusFilter' style={{ fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'block' }}>Status:</label>
                    <select
                      id='statusFilter'
                      className='form-control form-control-sm d-inline-block w-auto'
                      style={{ minWidth: 100, borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', cursor: 'pointer' }}
                      value={activeTab}
                      onChange={e => setActiveTab(e.target.value)}
                    >
                      {statusOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  {/* Sortowanie */}
                  <div className='form-group mb-0' style={{ minWidth: 100 }}>
                    <label htmlFor='sortBy' style={{ fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'block' }}>Sortuj według:</label>
                    <select
                      id='sortBy'
                      className='form-control form-control-sm d-inline-block w-auto'
                      style={{ minWidth: 100, borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', cursor: 'pointer' }}
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value)}
                    >
                      {sortOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className='form-group mb-0' style={{ minWidth: 80 }}>
                    <label htmlFor='sortOrder' style={{ fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'block' }}>Kierunek:</label>
                    <select
                      id='sortOrder'
                      className='form-control form-control-sm d-inline-block w-auto'
                      style={{ minWidth: 80, borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', cursor: 'pointer' }}
                      value={sortOrder}
                      onChange={e => setSortOrder(e.target.value)}
                    >
                      <option value='asc'>Rosnąco</option>
                      <option value='desc'>Malejąco</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            {/* Przycisk Utwórz projekt */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', margin: '32px 0 24px 0' }}>
              <button
                onClick={() => setShowNewProjectModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: 'none',
                  padding: 0
                }}
                onMouseEnter={e => {
                  const circle = e.currentTarget.querySelector('.circle') as HTMLElement | null;
                  const text = e.currentTarget.querySelector('.text') as HTMLElement | null;
                  if (circle) {
                    circle.style.borderColor = '#47a145';
                    circle.style.color = '#47a145';
                  }
                  if (text) {
                    text.style.color = '#47a145';
                  }
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
                onMouseLeave={e => {
                  const circle = e.currentTarget.querySelector('.circle') as HTMLElement | null;
                  const text = e.currentTarget.querySelector('.text') as HTMLElement | null;
                  if (circle) {
                    circle.style.borderColor = '#b0b0b0';
                    circle.style.color = '#b0b0b0';
                  }
                  if (text) {
                    text.style.color = '#b0b0b0';
                  }
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                <span
                  className="circle"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 64,
                    height: 64,
                    border: '4px solid #b0b0b0',
                    borderRadius: '50%',
                    color: '#b0b0b0',
                    fontSize: 40,
                    fontWeight: 400,
                    marginRight: 16,
                    transition: 'all 0.2s'
                  }}
                >
                  +
                </span>
                <span
                  className="text"
                  style={{
                    color: '#b0b0b0',
                    fontSize: 28,
                    fontWeight: 400,
                    textAlign: 'left',
                    lineHeight: 1.1,
                    transition: 'all 0.2s'
                  }}
                >
                  Utwórz<br />Projekt
                </span>
              </button>
            </div>

            <div className='row'>
              {projects.length > 0 ? (
                projects.map((project) => (
                  <div key={project.id} className='col-sm-12 col-md-6 col-lg-4 mb-4'>
                    <div className='task-board-card project-card' style={{ position: 'relative' }}>
                      {/* Przyciski akcji w prawym górnym rogu */}
                      {!project.isCollaborator && (
                        <div className='task-board-actions project-actions'>
                          <button
                            className='task-board-action-btn edit copy-btn'
                            onClick={(e) => { e.stopPropagation(); handleCopyProject(project.id) }}
                            title='Kopiuj projekt'
                          >
                            <i className='fa fa-copy' />
                          </button>
                          <button
                            className='task-board-action-btn delete delete-btn'
                            onClick={(e) => { e.stopPropagation(); handleDeleteProject(project.id) }}
                            title='Usuń projekt'
                          >
                            <i className='fa fa-trash' />
                          </button>
                        </div>
                      )}
                      <div
                        className='card-body d-flex flex-column'
                        style={{ cursor: 'pointer', height: '100%' }}
                        onClick={() => navigate(`/projects/${project.id}?tab=products`)}
                      >
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <h3 className='card-title mb-0'>
                            {project.name}
                            {project.isCollaborator && (
                              <span className='badge badge-info' title={`Właściciel: ${project.ownerEmail}`} style={{ marginLeft: 8 }}>
                                Współdzielony
                              </span>
                            )}
                          </h3>
                        </div>
                        <div className='card-text flex-grow-1' style={{ minHeight: 40 }}>
                          <p><strong>Elementy:</strong> {project.itemsCount || 0}</p>
                          {project.clientName && (
                            <p><strong>Inwestor:</strong> {project.clientName}</p>
                          )}
                          {project.budgetUsage != null && (
                            <div className='mb-10'>
                              <Box sx={{ width: '100%', mb: 1 }}>
                                <Typography variant='body2' color='text.secondary' gutterBottom>
                                  Budżet: {project.budget?.toLocaleString('pl-PL')} PLN
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box sx={{ width: '100%', mr: 1 }}>
                                    <LinearProgress
                                      variant='determinate'
                                      value={Math.min(project.budgetUsage.usagePercentage, 100)}
                                      sx={{
                                        height: 10,
                                        borderRadius: 5,
                                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: project.budgetUsage.usagePercentage > 100 ? '#d32f2f' : '#2e7d32',
                                          borderRadius: 5
                                        }
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ minWidth: 50 }}>
                                    <Typography variant='body2' color='text.secondary'>
                                      {project.budgetUsage.usagePercentage.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </div>
                          )}
                          <p>
                            <strong>Status:</strong>
                            <span className={`badge ${project.status === 'completed' ? 'badge-success' : 'badge-primary'}`}>
                              {project.status === 'completed' ? 'Zakończony' : 'W realizacji'}
                            </span>
                          </p>
                          <p className='mb-0'>
                            <strong>Udostępnienie:</strong>
                            {project.isShared
                              ? <span className='badge badge-success'>Udostępniony</span>
                              : <span className='badge badge-secondary'>Prywatny</span>}
                          </p>
                        </div>
                        <div className='mt-3 pt-3 border-top'>
                          <small className='text-muted'>
                            Właściciel: {project.username || '-'}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (<></>)}
            </div>
          </div>
        </section>
      </div>

      {/* Modal do tworzenia projektu */}
      {showNewProjectModal && (
        <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Utwórz nowy projekt</h5>
                <button type='button' className='close' onClick={() => setShowNewProjectModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className='modal-body'>
                <div className='form-group'>
                  <label>Nazwa projektu</label>
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Wprowadź nazwę projektu'
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateProject();
                      }
                    }}
                  />
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  className={`
                    tw-text-center tw-font-semibold tw-rounded-md tw-shadow-lg tw-transition-colors
                    tw-text-teal
                    tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-white hover:tw-bg-opacity-90 focus:tw-bg-white
                    tw-min-w-[120px] tw-flex tw-items-center tw-justify-center
                  `}
                  onClick={() => setShowNewProjectModal(false)}
                >
                  Anuluj
                </button>
                <button
                  className={`
                    tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                    tw-text-white tw-min-w-[120px]
                    tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                    tw-flex tw-items-center tw-justify-center tw-gap-2
                    ${!newProjectName.trim() ? 'tw-opacity-75' : ''}
                  `}
                  onClick={handleCreateProject}
                  disabled={!newProjectName.trim()}
                >
                  Utwórz projekt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ProjectsList
